import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import {
  DeleteButton,
  ImageInput,
  ImageItem,
  ImagesInfo,
  ImagesList,
  ImagesListItem,
  ImageTypes,
  MainImage,
  StyledInputLabel,
  UploadedImages,
  UploadWrapper,
  UploadingProgress,
  UploadingText,
  Loader,
  Text,
  CropperWrapper,
} from './styles';
import { ReactComponent as AttentionIcon } from 'applicaiton/assets/attention.svg';
import { ReactComponent as RemoveIcon } from 'applicaiton/assets/close.svg';
import { ReactComponent as LoadIcon } from 'applicaiton/assets/loader.svg';
import { ReactComponent as InfoIcon } from 'applicaiton/assets/info.svg';
import { SecondaryButton } from '../index';
import { ReactComponent as UploadIcon } from 'applicaiton/assets/upload.svg';
import { useTranslation } from 'react-i18next';
import { Photo } from 'common/types/clinics';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { uploadNewPhoto } from 'applicaiton/store/reducers/Photo/ActionCreators';
import {
  deletePhoto,
  uploadedPhoto,
} from 'applicaiton/store/reducers/Photo/PhotoSlice';
import { PhotoUploaderMultiProps } from './modules';
import { ModalComponent } from '../Modal';
import PrimaryButton from '../PrimaryButton';
import { dataUrlToFileUsingFetch } from 'common/helpers/dataUrlToFileUsingFetch';

/**
 * A component for uploading multiple images with the ability to crop them.
 *
 * @param currentPhotos - An array of currently uploaded photos.
 * @param setCurrentPhotos - A function to update the array of currently uploaded photos.
 * @param label - The label text to display above the image input.
 * @param isDeprecateMainPhotoDeletion - Whether deprecating the deletion of the main photo is enabled. Default value is true.
 * @param disableEditing - Whether editing (such as cropping) of images is disabled. Default value is false.
 * @param disabled - Whether the component is disabled and cannot be interacted with. Default value is false.
 * @param showRequiredIcon - Whether to show a required icon next to the label text. Default value is false.
 */
const PhotoUploaderMultiCrop: FC<PhotoUploaderMultiProps> = ({
  currentPhotos,
  setCurrentPhotos,
  label,
  isDeprecateMainPhotoDeletion = true,
  disableEditing = false,
  disabled,
  showRequiredIcon,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { uploadedPhotos, isLoading, isError } = useAppSelector(
    (state) => state.photos,
  );

  const [image, setImage] = useState<string>('');
  const [cropper, setCropper] = useState<Cropper>();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [showCropperModal, setShowCropperModal] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(uploadedPhoto([]));
    };
  }, []);

  useEffect(() => {
    const filteredPhotos = uploadedPhotos.filter((photo) => {
      const findPhoto = currentPhotos.find((item) => item.key === photo.key);
      if (findPhoto) return false;
      return true;
    });
    if (!currentPhotos.length) {
      return setCurrentPhotos(
        filteredPhotos.map((item, index) =>
          index === 0 ? { ...item, isMain: true } : item,
        ),
      );
    }
    setCurrentPhotos([...currentPhotos, ...filteredPhotos]);
  }, [uploadedPhotos]);

  /**
   * Handles changes to the image input.
   *
   * @param e - The change event object.
   */
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const files = e.target.files;
    if (files?.length) {
      const reader = new FileReader();
      setIsImageLoading(true);
      reader.onload = () => {
        setImage(reader.result as string);
      };
      reader.onloadend = () => {
        setShowCropperModal(true);
        setIsImageLoading(false);
      };

      reader.readAsDataURL(files[0]);
    }
  };

  /**
   * Gets the cropped image data and uploads it.
   */
  const getCropData = async () => {
    if (!cropper) return;
    const file = await dataUrlToFileUsingFetch(
      cropper?.getCroppedCanvas().toDataURL(),
      'image',
      'image/png',
    );
    setShowCropperModal(false);
    handleImageUpload(file);
  };

  /**
   * Handles uploading of an image.
   *
   * @param file - The image file to upload.
   */
  const handleImageUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('files', file);
    await dispatch(uploadNewPhoto(formData));
  };

  /**
   * Handles the end of a drag-and-drop operation.
   *
   * @param result - The drop result object from react-beautiful-dnd.
   */
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !currentPhotos) {
      return;
    }

    const resultList: Photo[] = Array.from(currentPhotos);
    const [removed] = resultList.splice(result.source.index, 1);

    resultList.splice(result.destination.index, 0, removed);

    const arr = resultList!.map((image, ind) => {
      return {
        ...image,
        isMain: ind === 0,
      };
    });

    setCurrentPhotos(arr);
  };

  /**
   * Handles deletion of an image.
   *
   * @param img - The photo to delete.
   */
  const handleDeleteImage = (img: Photo) => {
    const filteredPhotos = currentPhotos.filter(
      (image) => image.photoUrl !== img.photoUrl,
    );
    setCurrentPhotos(
      filteredPhotos.map((photo, index) => ({ ...photo, isMain: index === 0 })),
    );

    if (img.id) {
      dispatch(deletePhoto(String(img.id)));
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div>
        <StyledInputLabel shrink htmlFor="clinicPhoto">
          <Text>{label || t('upload_photo')}</Text>
          {showRequiredIcon && <InfoIcon />}
        </StyledInputLabel>
        {currentPhotos.length > 0 && (
          <UploadedImages>
            <ImagesInfo>
              <AttentionIcon />
              {t('editProfileSettings.firstPhoto')}
            </ImagesInfo>
            <Droppable
              droppableId="droppable"
              direction="horizontal"
              isDropDisabled={disabled}
            >
              {(provided) => (
                <ImagesList
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {currentPhotos.map((img, ind) => (
                    <Draggable
                      key={img.key}
                      draggableId={img.key!}
                      index={ind}
                      isDragDisabled={disabled}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <ImagesListItem isMain={ind === 0}>
                            {ind === 0 && (
                              <MainImage>{t('mainPhoto')}</MainImage>
                            )}
                            <ImageItem
                              src={img.thumbnailUrl}
                              alt={img.thumbnailUrl}
                            />
                            {(currentPhotos.length > 1 ||
                              !isDeprecateMainPhotoDeletion) &&
                            !disabled ? (
                              <DeleteButton
                                onClick={() => handleDeleteImage(img)}
                              >
                                <RemoveIcon />
                              </DeleteButton>
                            ) : (
                              ''
                            )}
                          </ImagesListItem>
                        </div>
                      )}
                    </Draggable>
                  ))}
                </ImagesList>
              )}
            </Droppable>
          </UploadedImages>
        )}
        {!!isLoading && (
          <UploadingProgress>
            <Loader>
              <LoadIcon />
            </Loader>
            <UploadingText>
              <p>{t('editProfileSettings.uploadingPhotos')}</p>
              <p>{t('editProfileSettings.wait')}</p>
            </UploadingText>
          </UploadingProgress>
        )}
        {!!isError && (
          <UploadingProgress>
            <Loader isError={isError}>
              <AttentionIcon />
            </Loader>
            <UploadingText isError={isError}>
              <p>{t('editProfileSettings.errorTitle')}</p>
              <p>{t('editProfileSettings.errorMessage')}</p>
            </UploadingText>
          </UploadingProgress>
        )}
        <UploadWrapper>
          <SecondaryButton
            styleType="color"
            disabled={disableEditing || disabled}
          >
            <ImageInput
              type="file"
              accept=".jpg, .jpeg, .png"
              onChange={onChange}
              disabled={isLoading || disableEditing || disabled}
            />
            <UploadIcon />
            {t('editProfileSettings.uploadPhoto')}
          </SecondaryButton>
          <ImageTypes>PNG, JPG, JPEG (max. 800x400px)</ImageTypes>
        </UploadWrapper>
      </div>
      <ModalComponent
        isOpen={showCropperModal && !isImageLoading}
        maxWidth={840}
        hideCloseIcon={true}
      >
        <CropperWrapper>
          <Cropper
            style={{
              minHeight: 300,
              minWidth: 300,
              maxHeight: 400,
              maxWidth: 800,
            }}
            initialAspectRatio={300 / 300}
            aspectRatio={300 / 300}
            src={image}
            viewMode={1}
            minCropBoxHeight={300}
            minCropBoxWidth={300}
            background={false}
            responsive={true}
            autoCropArea={1}
            checkOrientation={false}
            guides={true}
            onInitialized={(instance) => {
              setCropper(instance);
            }}
          />
          <PrimaryButton type="button" onClick={getCropData}>
            {t('crop_image')}
          </PrimaryButton>
        </CropperWrapper>
      </ModalComponent>
    </DragDropContext>
  );
};

export default PhotoUploaderMultiCrop;
